import {
  type SCChecklist,
  SCChecklistAnswer,
  SCChecklistRequest,
  SCSaveChecklistAnswersRequest,
  ServiceChannelClient,
} from '@kalos/kalos-rpc';
import { useCallback, useEffect, useState } from 'react';

import { ENDPOINT } from '../../../../../constants';

/**
 * How a question is answered is decided by whether it carries options, NOT by
 * its type number.
 *
 * Keying on the type was wrong and would have shipped. A sweep of 10 live
 * subscriber checklists found types 0, 1, 2 and 4 in use, and 0, 2 and 4 all
 * carry no options — type 0 is an acknowledgement ("Allow compressor to run
 * for a minimum of 30 minutes..."), 2 and 4 are free text. Treating only
 * type 4 as free text rendered the others as an empty option list: a question
 * with no input at all. A required one would then block completion forever,
 * turning the gate into a trap.
 *
 * Options present means choose from them; none means type an answer. That
 * holds for every type observed and degrades safely for any we have not seen.
 */
export const isFreeTextQuestion = (q: { options: unknown[] }) => q.options.length === 0;

/** Answers keyed by question id, as the form holds them while being edited. */
export type ChecklistDraft = Record<number, { optionIds: number[]; freeText: string }>;

/**
 * Whether a checklist blocks completion, and why.
 *
 * `loaded` matters as much as `complete`. A job whose checklist has not been
 * fetched yet — or whose fetch failed — is NOT known to be answered, and
 * treating that as "nothing required" would let a tech complete a job whose
 * checklist they were never shown. So completion is blocked until we have a
 * definite answer either way.
 */
export type ChecklistGate = {
  loaded: boolean;
  failed: boolean;
  /** True when the job has no checklist at all, which is the common case. */
  notApplicable: boolean;
  complete: boolean;
  /** Questions still needing an answer, for the message shown to the tech. */
  unanswered: string[];
};

const buildDraft = (checklists: SCChecklist[]): ChecklistDraft => {
  const draft: ChecklistDraft = {};
  for (const list of checklists) {
    for (const q of list.questions) {
      draft[q.id] = {
        optionIds: [...q.selectedOptionIds],
        freeText: q.freeTextAnswer,
      };
    }
  }
  return draft;
};

/** A required question is answered when it has a selection or non-blank text. */
const isAnswered = (
  question: { options: unknown[] },
  answer?: { optionIds: number[]; freeText: string },
) => {
  if (!answer) return false;
  if (isFreeTextQuestion(question)) {
    return answer.freeText.trim().length > 0;
  }
  return answer.optionIds.length > 0;
};

/**
 * Loads a job's ServiceChannel checklist, holds the tech's in-progress answers,
 * and reports whether completion may proceed.
 *
 * Jobs that are not ServiceChannel jobs return no checklists, so this is safe
 * to call for every job — the gate simply reports notApplicable.
 */
export const useScChecklist = (eventId: number, loggedUserId: number) => {
  const [checklists, setChecklists] = useState<SCChecklist[]>([]);
  const [draft, setDraft] = useState<ChecklistDraft>({});
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    const client = new ServiceChannelClient(ENDPOINT);
    try {
      const req = SCChecklistRequest.create({ eventId });
      const res = await client.GetChecklist(req);
      setChecklists(res.checklists);
      setDraft(buildDraft(res.checklists));
      setFailed(false);
    } catch (err) {
      // Deliberately not swallowed into an empty list: "we could not ask" is
      // not the same as "there is nothing to answer", and the gate below
      // refuses completion rather than assuming the permissive reading.
      console.error('failed to load the ServiceChannel checklist', err);
      setFailed(true);
    } finally {
      setLoaded(true);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const setAnswer = useCallback(
    (questionId: number, next: { optionIds: number[]; freeText: string }) => {
      setDraft((prev) => ({ ...prev, [questionId]: next }));
    },
    [],
  );

  const save = useCallback(async () => {
    const client = new ServiceChannelClient(ENDPOINT);
    setSaving(true);
    try {
      for (const list of checklists) {
        const answers: SCChecklistAnswer[] = [];
        for (const q of list.questions) {
          const entry = draft[q.id];
          if (!entry) continue;
          if (isFreeTextQuestion(q)) {
            if (entry.freeText.trim().length === 0) continue;
            answers.push(
              SCChecklistAnswer.create({
                questionId: q.id,
                optionId: 0,
                freeTextAnswer: entry.freeText,
              }),
            );
            continue;
          }
          // A multi-select question sends one answer per chosen option; the
          // backend replaces the question's answers wholesale, so deselecting
          // is expressed by simply not sending that option.
          for (const optionId of entry.optionIds) {
            answers.push(SCChecklistAnswer.create({ questionId: q.id, optionId }));
          }
        }
        if (answers.length === 0) continue;
        await client.SaveChecklistAnswers(
          SCSaveChecklistAnswersRequest.create({
            eventId,
            checklistId: list.id,
            answers,
            answeredByUserId: loggedUserId,
          }),
        );
      }
      await load();
    } finally {
      setSaving(false);
    }
  }, [checklists, draft, eventId, loggedUserId, load]);

  const unanswered: string[] = [];
  for (const list of checklists) {
    for (const q of list.questions) {
      if (q.answerRequired && !isAnswered(q, draft[q.id])) {
        unanswered.push(q.question);
      }
    }
  }

  const gate: ChecklistGate = {
    loaded,
    failed,
    notApplicable: loaded && !failed && checklists.length === 0,
    // Unloaded or failed is never "complete" — see ChecklistGate.
    complete: loaded && !failed && unanswered.length === 0,
    unanswered,
  };

  return { checklists, draft, gate, saving, setAnswer, save, reload: load };
};
